import { Injectable, inject } from '@angular/core';
import { GroupPrivacyLevel } from '@app/groups/group-api';
import { GroupService } from '@app/groups/services/group.service';
import type {
  GroupSearchResolver,
  GroupSummary,
} from '@degreed/lxp/ui/content';
import { Observable, map, of } from 'rxjs';

/**
 * Concrete implementation of GROUP_SEARCH_RESOLVER for the LXP app.
 * Delegates to GroupService.searchGroups (calls /organizations/searchgroups)
 * and projects the legacy `Group[]` shape into the lib-side `GroupSummary[]`
 * contract.
 *
 * The `searchGroupPreset` (sent as the privacy-level arg) varies by the
 * visibility mode the field is in:
 *   - Specific Groups (`administeredOnly`) → `Administrative` (preset 3):
 *     only groups the user administers, matching the legacy advanced-settings
 *     "specific groups" restriction.
 *   - Entire Organization boost → `Closed` (preset 1 = visibleToUserGroups):
 *     all groups visible to the user, matching the peer-search convention.
 */
@Injectable()
export class CatalogGroupSearchResolverService implements GroupSearchResolver {
  private readonly groupService = inject(GroupService);

  public search(
    term: string,
    organizationId: number | null | undefined,
    administeredOnly: boolean
  ): Observable<GroupSummary[]> {
    if (!organizationId) return of([]);
    const preset = administeredOnly
      ? GroupPrivacyLevel.Administrative
      : GroupPrivacyLevel.Closed;
    return this.groupService
      .searchGroups(organizationId, preset, term)
      .pipe(
        map((groups) =>
          groups.map((g) => ({ groupId: g.groupId, name: g.name }))
        )
      );
  }
}
